import { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import { TaxCase, TaxCaseDoc, TaxStatus, UserRole } from '../types';
import { MOCK_DEMO_CASES } from '../services/firestoreService';

export type StaffFilter = 'my_open' | 'my_completed' | 'firm_open' | 'firm_completed';

interface UseStaffTaxCasesResult {
  cases: TaxCase[];
  loading: boolean;
  error: string | null;
}

export const useStaffTaxCases = (
  staffId: string | undefined, 
  staffRole: UserRole | undefined, 
  filter: StaffFilter,
  locationId?: string | null
): UseStaffTaxCasesResult => {
  const [cases, setCases] = useState<TaxCase[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!staffId || !staffRole) {
      setLoading(false);
      return;
    }

    // DEMO MODE BYPASS
    if (locationId === 'hannah-financial-demo') {
      let filtered = [...MOCK_DEMO_CASES];
      const activeStatuses = [TaxStatus.INFO_RECEIVED, TaxStatus.IN_PROGRESS, TaxStatus.IN_REVIEW, TaxStatus.READY_TO_SIGN];
      const completedStatuses = [TaxStatus.FILED, TaxStatus.ACCEPTED];

      if (filter === 'my_open') {
        filtered = filtered.filter(c => activeStatuses.includes(c.status) && c.assignedStaffId === staffId);
      } else if (filter === 'my_completed') {
        filtered = filtered.filter(c => completedStatuses.includes(c.status) && c.assignedStaffId === staffId);
      } else if (filter === 'firm_open') {
        filtered = filtered.filter(c => activeStatuses.includes(c.status));
      } else if (filter === 'firm_completed') {
        filtered = filtered.filter(c => completedStatuses.includes(c.status));
      }

      setCases(filtered);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const activeStatuses = [TaxStatus.INFO_RECEIVED, TaxStatus.IN_PROGRESS, TaxStatus.IN_REVIEW, TaxStatus.READY_TO_SIGN];
    const completedStatuses = [TaxStatus.FILED, TaxStatus.ACCEPTED];

    try {
        const casesRef = collection(db, 'firms', locationId || 'default', 'cases');
        let constraints: any[] = [];

        switch (filter) {
            case 'my_open':
                constraints.push(where('assignedStaffId', '==', staffId));
                constraints.push(where('status', 'in', activeStatuses));
                break;
            case 'my_completed':
                constraints.push(where('assignedStaffId', '==', staffId));
                constraints.push(where('status', 'in', completedStatuses));
                break;
            case 'firm_open':
                constraints.push(where('status', 'in', activeStatuses));
                break;
            case 'firm_completed':
                constraints.push(where('status', 'in', completedStatuses));
                break;
        }

        const q = query(casesRef, ...constraints, orderBy('lastUpdated', 'desc'));

        const unsubscribe = onSnapshot(q, 
          (snapshot) => {
            const results: TaxCase[] = snapshot.docs.map(doc => {
              const data = doc.data() as TaxCaseDoc;
              return {
                ...data,
                id: doc.id,
                status: data.status as TaxStatus,
                lastUpdated: data.lastUpdated
              };
            });
            setCases(results);
            setLoading(false);
          },
          (err) => {
            console.error("Error fetching staff tax cases:", err);
            setLoading(false);
          }
        );
        return () => unsubscribe();
    } catch(e) {
        setLoading(false);
    }
  }, [staffId, staffRole, filter, locationId]);

  return { cases, loading, error };
};
